import type { WorkflowApi } from '../api/WorkflowApi'
import type { WorkflowExecution, WorkflowSchedule, WorkflowScope } from '../models/workflow.types'

import type { CreditGuard } from '@/features/credits'
import { ApiError } from '@/shared/http'

export type RunWorkflowResult =
  | { kind: 'queued'; execution: WorkflowExecution }
  | { kind: 'depleted' }
  | { kind: 'error'; error: ApiError }

export interface RunWorkflowFlowOptions {
  api: Pick<WorkflowApi, 'runNow'>
  creditGuard: CreditGuard
  notifySuccess: () => void
  reload: () => Promise<void>
}

/** Truthful fire-and-poll run: queued is success; no fabricated progress or completion state. */
export class RunWorkflowFlow {
  constructor(private readonly options: RunWorkflowFlowOptions) {}

  async execute(scope: WorkflowScope, schedule: WorkflowSchedule): Promise<RunWorkflowResult> {
    try {
      const execution = await this.options.api.runNow(scope, schedule.id)
      this.options.notifySuccess()
      await Promise.all([this.options.creditGuard.refreshBalance(), this.options.reload()])
      return { kind: 'queued', execution }
    } catch (cause) {
      if (this.options.creditGuard.handleDepletion(cause)) return { kind: 'depleted' }
      return {
        kind: 'error',
        error: ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause),
      }
    }
  }
}
